/**
 * Email Editor Tab Component
 *
 * Rich text email editor using the existing EmailEditorCore component.
 * Provides WYSIWYG editing capabilities for composing HTML emails.
 */

import React, { useRef, useImperativeHandle, forwardRef } from "react";
import { EmailEditorCore, EmailEditorCoreRef } from "../../../../../shared/email-editor-core";

export interface EditorTabRef {
  getHtml: () => Promise<{ html: string; design: object }>;
  loadDesign: (design: object) => void;
}

interface EditorTabProps {
  onHtmlChange?: (html: string, design: object) => void;
  initialDesign?: object;
  minHeight?: string;
}

export const EditorTab = forwardRef<EditorTabRef, EditorTabProps>(
  ({ onHtmlChange, initialDesign, minHeight = "400px" }, ref) => {
    const editorRef = useRef<EmailEditorCoreRef>(null);
    const isInitialized = useRef(false);

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      getHtml: () => {
        return new Promise((resolve) => {
          editorRef.current?.exportHtml((data) => {
            resolve({ html: data.html, design: data.design });
          });
        });
      },
      loadDesign: (design: object) => {
        editorRef.current?.loadDesign(design);
      },
    }));

    const handleEditorReady = () => {
      // Load initial design if provided
      if (initialDesign && !isInitialized.current) {
        editorRef.current?.loadDesign(initialDesign);
        isInitialized.current = true;
      }
    };

    return (
      <div className="h-full w-full bg-white">
        <EmailEditorCore
          ref={editorRef}
          minHeight={minHeight}
          onReady={handleEditorReady}
        />
      </div>
    );
  }
);

EditorTab.displayName = "EditorTab";
